import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Trash2, ListTodo, Paperclip, FileText, Download } from 'lucide-react';
import { CRMMarkdown } from './crmMarkdown';
import { crmNotesAPI } from '@/lib/api';
import { formatDate, isKRRole } from '@/lib/utils';
import { toast } from 'sonner';

const TYPE_COLORS = {
  call: 'bg-sky-100 text-sky-800',
  email: 'bg-violet-100 text-violet-800',
  meeting: 'bg-emerald-100 text-emerald-800',
  internal: 'bg-slate-100 text-slate-800',
  contracting: 'bg-amber-100 text-amber-800',
};

function AttachmentTile({ noteId, att }) {
  const isImg = (att.content_type || '').startsWith('image/');
  const isPdf = att.content_type === 'application/pdf';
  const url = crmNotesAPI.attachmentUrl(noteId, att.attachment_id);
  const token = localStorage.getItem('token') || '';
  const authedUrl = `${process.env.REACT_APP_BACKEND_URL}/api${url}`;

  const [blobUrl, setBlobUrl] = React.useState(null);
  React.useEffect(() => {
    let revoked = false;
    if (!isImg && !isPdf) return;
    // Fetch with token, convert to blob so <img>/<embed> can render.
    fetch(authedUrl, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => {
        if (revoked) return;
        setBlobUrl(URL.createObjectURL(b));
      })
      .catch(() => setBlobUrl(null));
    return () => { revoked = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [authedUrl, isImg, isPdf]);

  const download = async () => {
    try {
      const r = await fetch(authedUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Download failed');
      const b = await r.blob();
      const dlUrl = URL.createObjectURL(b);
      const a = document.createElement('a');
      a.href = dlUrl; a.download = att.original_filename || 'download'; a.click();
      URL.revokeObjectURL(dlUrl);
    } catch { toast.error('Failed to download'); }
  };

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden bg-slate-50" data-testid={`crm-note-attachment-${att.attachment_id}`}>
      {isImg && blobUrl && (
        <img src={blobUrl} alt={att.original_filename} loading="lazy"
             className="w-full max-h-72 object-contain bg-white" />
      )}
      {isPdf && blobUrl && (
        <embed src={blobUrl} type="application/pdf" className="w-full h-72" />
      )}
      <div className="flex items-center justify-between px-3 py-2 gap-2">
        <div className="min-w-0">
          <div className="text-xs text-slate-700 truncate flex items-center gap-1">
            <FileText size={12} /> {att.original_filename}
          </div>
          <div className="text-[10px] text-slate-500">
            {(att.size / 1024).toFixed(1)} KB · {att.content_type}
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={download} className="h-7 px-2">
          <Download size={12} className="mr-1" /> Download
        </Button>
      </div>
    </div>
  );
}

export function NoteCard({ note, currentUser, onCreateFollowup, onDeleted }) {
  const isDeleted = !!note.deleted_at;
  const isKR = isKRRole(currentUser);
  const [showDelete, setShowDelete] = useState(false);
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    if ((reason || '').trim().length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    setDeleting(true);
    try {
      await crmNotesAPI.remove(note.note_id, reason.trim());
      toast.success('Note deleted');
      setShowDelete(false);
      onDeleted?.(note.note_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card data-testid={`crm-note-card-${note.note_id}`}
          className={`transition ${isDeleted ? 'opacity-60 border-dashed' : ''}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <Badge className={TYPE_COLORS[note.note_type] || 'bg-slate-100'}
                   data-testid={`crm-note-type-${note.note_id}`}>
              {note.note_type}
            </Badge>
            {note.subject_type === 'agent' && (
              <Badge variant="outline" data-testid={`crm-note-visibility-${note.note_id}`}
                     className={note.visible_to_agent ? 'border-emerald-400 text-emerald-700' : 'border-slate-300 text-slate-500'}>
                {note.visible_to_agent ? 'Visible to Agent' : 'Not shared with Agent'}
              </Badge>
            )}
            <span className="text-xs text-slate-500 truncate">
              {note.author_name} · {formatDate(note.created_at)}
            </span>
          </div>
          {!isDeleted && (
            <div className="flex items-center gap-1 shrink-0">
              <Button size="sm" variant="ghost" className="h-7 px-2"
                      onClick={() => onCreateFollowup?.(note)}
                      data-testid={`crm-note-create-followup-${note.note_id}`}>
                <ListTodo size={14} className="mr-1" /> Follow-up
              </Button>
              {isKR && (
                <Button size="sm" variant="ghost"
                        className="h-7 px-2 text-red-600 hover:text-red-700"
                        onClick={() => setShowDelete(true)}
                        data-testid={`crm-note-delete-${note.note_id}`}>
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          )}
        </div>

        {isDeleted ? (
          <div className="text-xs text-slate-500 italic">
            Deleted {formatDate(note.deleted_at)} by {note.deleted_by_name}
            {isKR && note.deletion_reason && (
              <> — <span className="text-slate-700">{note.deletion_reason}</span></>
            )}
          </div>
        ) : (
          <>
            <CRMMarkdown className="text-sm text-slate-800 whitespace-pre-wrap">
              {note.body}
            </CRMMarkdown>
            {(note.attachments || []).length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                <div className="col-span-full text-xs text-slate-500 flex items-center gap-1">
                  <Paperclip size={12} /> Attachments
                </div>
                {(note.attachments || []).map((a) => (
                  <AttachmentTile key={a.attachment_id} noteId={note.note_id} att={a} />
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>

      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent data-testid="delete-note-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription>
              This note will be permanently redacted from the CRM feed. This action requires
              a mandatory reason and cannot be undone. An audit stub will remain.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="Reason for deletion (minimum 5 characters)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            data-testid="delete-note-reason"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting || reason.trim().length < 5}
              onClick={(e) => { e.preventDefault(); doDelete(); }}
              data-testid="delete-note-confirm"
              className="bg-red-600 hover:bg-red-700">
              {deleting ? 'Deleting…' : 'Delete Note'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default NoteCard;
